import { useGetChildrenList } from 'common/queries-and-mutations/children';
import { useGetSpousesList } from 'common/queries-and-mutations/spouse';
import Badge from 'components/Badge';
import Button from 'components/Form/Button';
import React, { useCallback, useMemo, useState } from 'react';
import male from '../../assets/male.png';
import female from '../../assets/female.png';
import { Gender } from 'apps/user/types/user.enum.type';
import { InfoModal } from './InfoModal';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';

export interface SpouseChildDto {
  id: string;
  name: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  gender: string;
  relationship: string;
  address: string;
  dateOfBirth: string;
  isAssigned: boolean;
}

type Props = {
  onClose: () => void;
  onAdd: (selectUsers: SpouseChildDto[]) => void;
};

export default function SelectSingleBeneficiaryModal({
  onClose,
  onAdd
}: Props) {
  const [selectedUser, setSelectedUser] = useState<SpouseChildDto | null>(null);

  const { data: childrens } = useGetChildrenList();
  const { data: spouses } = useGetSpousesList();

  const combinedTwoArray = useCallback(() => {
    const combinedArray: SpouseChildDto[] = [];
    childrens &&
      childrens.forEach(item => {
        combinedArray.push({
          id: `Child-${item.id!}`,
          name: `${item.childFirstName} ${item.childMiddleName} ${item.childLastName}`,
          firstName: item.childFirstName,
          middleName: item.childMiddleName,
          lastName: item.childLastName,
          email: '',
          relationship: 'Child',
          gender: item.gender,
          isAssigned: item.isAssigned,
          address: '',
          dateOfBirth: item.dateOfBirth
        });
      });

    spouses &&
      spouses.forEach(item => {
        combinedArray.push({
          id: `Spouse-${item.id!}`,
          name: `${item.spouseFirstName} ${item.spouseMiddleName} ${item.spouseLastName}`,
          firstName: item.spouseFirstName,
          middleName: item.spouseMiddleName,
          lastName: item.spouseLastName,
          email: item.emailAddress,
          relationship: 'Spouse',
          gender: item.gender,
          isAssigned: item.isAssigned,
          address: item.homeAddress,
          dateOfBirth: ''
        });
      });

    return combinedArray;
  }, [childrens, spouses]);

  const normalizeResult = useMemo(() => {
    return combinedTwoArray();
  }, [combinedTwoArray]);

  return (
    <InfoModal
      width="w-[60%]"
      className="h-fit rounded-md px-2 pb-9 pt-5 sm:p-4"
    >
      <div>
        <p className="mb-5 font-circular text-lg font-bold text-[#353535]">
          Spouse & Children
        </p>
        <table className="table-to-print w-full px-2 py-4 text-left text-sm">
          <thead className="border-b bg-white text-xs text-[#1F4173]">
            <tr>
              <th scope="col" className="px-6 py-4"></th>
              <th scope="col" className="px-6 py-4">
                Name
              </th>
              <th scope="col" className="px-6 py-4">
                Email
              </th>
              <th scope="col" className="px-6 py-4">
                Relationship
              </th>
              <th scope="col" className="px-6 py-4">
                Status
              </th>
              <th scope="col" className="px-6 py-4">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="px-6 py-4">
            {!normalizeResult?.length ? (
              <tr className="text-center">
                <td colSpan={5} className="py-10 text-lg">
                  No results found
                </td>
              </tr>
            ) : (
              normalizeResult?.map(user => (
                <tr
                  key={user.id}
                  className={`border-b ${
                    user.id === selectedUser?.id
                      ? 'bg-[#FF660012]'
                      : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <td className="h-full w-full px-6 py-4 text-xs font-normal  text-[#353535]">
                    <img
                      className="h-full w-full"
                      src={user.gender === Gender.FEMALE ? female : male}
                    />
                  </td>

                  <td className="whitespace-nowrap px-6 py-4 text-xs font-normal text-[#353535]">
                    {user.name}
                  </td>
                  <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                    {user.email || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                    {user.relationship || 'N/A'}
                  </td>
                  <td className="px-6 py-4">
                    <Badge
                      title={user.isAssigned ? 'Assigned' : 'Unassigned'}
                      type={user.isAssigned ? 'success' : 'danger'}
                      size="sm"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      variant="primary"
                      size="sm"
                      className="mb-3 w-[10rem] rounded px-[0.5rem] py-0.5 text-sm font-normal"
                      label="Select"
                      onClick={() => setSelectedUser(user)}
                    ></Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="mt-10 flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            size="md"
            className="mb-3 w-[10rem] rounded px-[1.5rem] py-1 text-sm font-normal"
            label="Cancel"
            type="button"
            onClick={onClose}
          ></Button>
          <Button
            variant="primary"
            size="md"
            className="mb-3 w-[10rem] rounded px-[1.5rem] py-1 text-sm font-normal"
            label="Add Beneficiary"
            onClick={() => {
              if (selectedUser) {
                onAdd([selectedUser]);
              } else {
                toast(
                  <Alert message="Please select a beneficiary" type="error" />
                );
              }
            }}
          ></Button>
        </div>
      </div>
    </InfoModal>
  );
}
